import { CutoverEvaluation } from "./forwarding";

/**
 * The pure half of the variant lookup: react-query state plus an untyped
 * response body in, the answer shouldForwardToV2 consumes out. Kept out of
 * the hook so every shape appconfig can return is covered by a test rather
 * than by a browser.
 */

/**
 * The rolled variant's name, or null when the toggle carries no variants at
 * all.
 *
 * Anything unreadable comes back as the empty string rather than null,
 * because null means "no variants configured" and forwards, while an
 * unrecognised name renders. A variant this app cannot read is a map and a
 * deploy that disagree, and that is not a reason to leave the page.
 */
export function readVariantName(variant: unknown): string | null {
  if (variant === null || variant === undefined) {
    return null;
  }

  if (
    typeof variant === "object" &&
    typeof (variant as { name?: unknown }).name === "string"
  ) {
    return (variant as { name: string }).name;
  }

  return "";
}

export function readEvaluation(
  status: string,
  data: unknown,
): CutoverEvaluation {
  if (status === "error") {
    return { status: "error" };
  }

  if (status !== "success") {
    return { status: "pending" };
  }

  // A success with nothing in it is not an answer. Api.fetch resolves
  // `undefined` when the body will not parse — a 204, or a 200 that is not
  // JSON — and react-query keeps that as a success, so reading it as pending
  // would hold the gate for the whole page load: nothing forwards and the
  // redirect stash never comes back on. It is an error, and errors render.
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { status: "error" };
  }

  const { enabled, variant } = data as Record<string, unknown>;

  // `enabled` is what the whole decision turns on; a body without it is some
  // other endpoint's answer, not this one's.
  if (typeof enabled !== "boolean") {
    return { status: "error" };
  }

  return { status: "ok", enabled, variant: readVariantName(variant) };
}

/**
 * The rotated pair from a response, or null.
 *
 * The session protocol (`with-new-session` plus the two token headers) is
 * normally carried by SessionGate's interceptor, which is mounted inside the
 * authenticated stack — below the cut-over gate, and only if the gate renders
 * it. On boot the gate's own evaluate call is usually the first authenticated
 * request there is, so it has to honour a rotation itself or the pair it
 * silently dropped 401s on the next call and takes both apps' sessions with
 * it.
 *
 * Half a pair is nothing: SessionGate logs out on that, but a caller that
 * runs before the session is armed has nothing to log out of, so it keeps the
 * tokens it has and lets the next call fail normally.
 */
export function readRotatedTokens(
  header: (name: string) => string | null,
): { accessToken: string; refreshToken: string } | null {
  if (header("with-new-session") !== "true") {
    return null;
  }

  const accessToken = header("access-token");
  const refreshToken = header("refresh-token");

  return accessToken && refreshToken ? { accessToken, refreshToken } : null;
}
